import { ThemeColors, useColors } from "@/constants/theme";
import { useAppStore } from "@/stores/useStore";
import { useRouter } from "expo-router";
import { Check, PlayCircle, Search } from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
import {
  FlatList,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native";
import { Searchbar, Text } from "react-native-paper";

export default function LibraryScreen() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const {
    cards,
    loadLibraryContent,
    subjects,
  } = useAppStore();
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const columnCount = width >= 1180 ? 3 : width >= 760 ? 2 : 1;
  const hasLibraryCards = cards.length > 0;

  useEffect(() => {
    loadLibraryContent().catch((error) => {
      console.warn("Failed to load library content", error);
    });
  }, [loadLibraryContent]);

  const filteredCards = cards.filter((card) => {
    const matchesSubject =
      !selectedSubjectId || card.subject_id === selectedSubjectId;
    const matchesSearch =
      card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      card.lens_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSubject && matchesSearch;
  });

  const handleCardPress = (cardId: string) => {
    router.push({
      pathname: "/cards/[cardId]",
      params: { cardId },
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text variant="headlineMedium" style={styles.title}>
          📚 学問レンズ図書館
        </Text>
        <Text variant="bodySmall" style={styles.subtitle}>
          すべての学問カードを探索しよう
        </Text>
      </View>

      <View style={styles.searchContainer}>
        <Searchbar
          placeholder="カード名で検索..."
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={styles.searchBar}
          icon={() => <Search size={20} color={c.textMuted} />}
        />
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabsContainer}
        contentContainerStyle={styles.tabsContent}
      >
        <Pressable
          accessibilityRole="button"
          onPress={() => setSelectedSubjectId(null)}
          style={[styles.chip, selectedSubjectId === null && styles.chipActive]}
        >
          {selectedSubjectId === null && <Check size={16} color={c.textPrimary} />}
          <Text
            style={[
              styles.chipText,
              selectedSubjectId === null && styles.chipTextActive,
            ]}
          >
            すべて
          </Text>
        </Pressable>
        {subjects.map((subject) => (
          <Pressable
            key={subject.id}
            accessibilityRole="button"
            onPress={() => setSelectedSubjectId(subject.id)}
            style={[
              styles.chip,
              { borderColor: subject.color_code },
              selectedSubjectId === subject.id && {
                backgroundColor: `${subject.color_code}14`,
                borderColor: subject.color_code,
              },
            ]}
          >
            {selectedSubjectId === subject.id && (
              <Check size={16} color={c.textPrimary} />
            )}
            <Text
              style={[
                styles.chipText,
                selectedSubjectId === subject.id && styles.chipTextActive,
              ]}
            >
              {subject.name}
            </Text>
          </Pressable>
        ))}
      </ScrollView>

      <FlatList
        key={`library-${columnCount}`}
        data={filteredCards}
        numColumns={columnCount}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => {
          const subject = subjects.find((s) => s.id === item.subject_id);
          return (
            <Pressable
              style={({ pressed }) => [
                styles.cardContainer,
                pressed && styles.cardPressed,
              ]}
              onPress={() => handleCardPress(item.id)}
            >
              <View
                style={[
                  styles.card,
                  {
                    borderLeftColor: subject?.color_code || c.border,
                    borderLeftWidth: 6,
                  },
                ]}
              >
                <View
                  style={[
                    styles.subjectBadge,
                    { backgroundColor: subject?.color_code + "20" },
                  ]}
                >
                  <Text
                    style={[
                      styles.subjectBadgeText,
                      { color: subject?.color_code },
                    ]}
                  >
                    {subject?.name}
                  </Text>
                </View>

                <Text
                  variant="titleMedium"
                  style={[styles.cardTitle, { color: subject?.color_code }]}
                >
                  {item.title}
                </Text>

                <Text variant="bodyMedium" style={styles.lensName}>
                  {item.lens_name}
                </Text>

                {!!item.video_urls?.length && (
                  <View style={styles.mediaBadge}>
                    <PlayCircle size={14} color={c.textSecondary} />
                    <Text style={styles.mediaBadgeText}>動画あり</Text>
                  </View>
                )}

                <Text
                  variant="bodySmall"
                  style={styles.definition}
                  numberOfLines={2}
                >
                  {item.definition}
                </Text>

                <View style={styles.tagsContainer}>
                  {item.thinking_tags.slice(0, 3).map((tag) => (
                    <View
                      key={tag}
                      style={[
                        styles.tag,
                        { backgroundColor: subject?.color_code + "15" },
                      ]}
                    >
                      <Text
                        style={[
                          styles.tagText,
                          { color: subject?.color_code || c.textPrimary },
                        ]}
                      >
                        #{tag}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            </Pressable>
          );
        }}
        contentContainerStyle={styles.listContent}
        columnWrapperStyle={columnCount > 1 ? styles.columnWrapper : undefined}
        scrollEnabled={true}
        ListHeaderComponent={
          <Text style={styles.resultCount}>
            {filteredCards.length}枚のレンズ
          </Text>
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {hasLibraryCards
                ? "該当するレンズが見つかりませんでした"
                : "学問レンズはまだ登録されていません"}
            </Text>
            <Text style={styles.emptySubtext}>
              {hasLibraryCards
                ? "検索ワードや科目フィルタを変えてみてください"
                : "管理サイトで追加されたレンズがここに表示されます"}
            </Text>
          </View>
        }
      />

    </View>
  );
}

const createStyles = (c: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.bg,
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
    backgroundColor: c.surface,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
    width: "100%",
    maxWidth: 1180,
    alignSelf: "center",
  },
  title: {
    fontWeight: "700",
    marginBottom: 4,
    color: c.textPrimary,
  },
  subtitle: {
    color: c.textSecondary,
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: c.surface,
    width: "100%",
    maxWidth: 1180,
    alignSelf: "center",
  },
  searchBar: {
    height: 40,
    backgroundColor: c.surfaceAlt,
  },
  tabsContainer: {
    backgroundColor: c.surface,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
    flexGrow: 0,
  },
  tabsContent: {
    width: "100%",
    maxWidth: 1180,
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
    alignItems: "center",
  },
  chip: {
    minWidth: 76,
    height: 38,
    borderRadius: 20,
    backgroundColor: c.surface,
    borderWidth: 1,
    borderColor: c.border,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  chipActive: {
    backgroundColor: c.primarySoft,
    borderColor: c.primary,
  },
  chipText: {
    fontSize: 13,
    fontWeight: "700",
    color: c.textPrimary,
    lineHeight: 18,
  },
  chipTextActive: {
    color: c.primaryDark,
  },
  listContent: {
    width: "100%",
    maxWidth: 1180,
    alignSelf: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  resultCount: {
    color: c.textMuted,
    fontSize: 12,
    fontWeight: "700",
    marginBottom: 10,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginTop: 48,
    paddingHorizontal: 24,
  },
  emptyText: {
    textAlign: "center",
    color: c.textSecondary,
    fontSize: 15,
    fontWeight: "700",
    marginBottom: 8,
  },
  emptySubtext: {
    textAlign: "center",
    color: c.textMuted,
    fontSize: 13,
  },
  cardContainer: {
    marginBottom: 12,
    flex: 1,
  },
  columnWrapper: {
    gap: 12,
  },
  cardPressed: {
    opacity: 0.7,
  },
  card: {
    backgroundColor: c.surface,
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
  },
  subjectBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginBottom: 8,
  },
  subjectBadgeText: {
    fontSize: 11,
    fontWeight: "700",
  },
  cardTitle: {
    fontWeight: "700",
    marginBottom: 4,
  },
  lensName: {
    fontStyle: "italic",
    color: c.textSecondary,
    marginBottom: 8,
    fontSize: 13,
  },
  mediaBadge: {
    alignSelf: "flex-start",
    minHeight: 28,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderRadius: 14,
    backgroundColor: c.surfaceAlt,
    paddingHorizontal: 10,
    marginBottom: 10,
  },
  mediaBadgeText: {
    color: c.textSecondary,
    fontSize: 12,
    fontWeight: "700",
  },
  definition: {
    color: c.textSecondary,
    lineHeight: 18,
    marginBottom: 10,
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  tag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  tagText: {
    fontSize: 12,
    fontWeight: "600",
  },
});
